import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useAppQuery} from '../../../../../libs/shared/core/src/services/query/useAppQuery';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import inboxNoConversations3 from '../../../../assets/images/inboxNoConversations3.png';
import successStartGuide4 from '../../../../assets/images/successStartGuide4.png';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {aiTaggingQueries} from '../../../../queries/aiTaggingStatsQueries';
import {deleteLensAsyncAction} from '../../../../store/data/lenses/saveLensActions';
import {StyledNoConversationsImg, StyledViewerBodyDiv, StyledViewerHeaderDiv} from './emptyInboxesStyles';

/*
 * Props.
 */

interface EmptyInboxesAiTaggingProps {
  lensNamespace: string;
  lensId: number;
  reviewingAiTagId: number | undefined;
}

/*
 * Style.
 */

const StyledViewerFooterDiv = styled.div`
  display: flex;
  justify-content: center;

  margin-top: 16px;
`;

const StyledAllDoneImg = styled.img`
  margin-bottom: 20px;
`;

/*
 * Component.
 */

export const EmptyInboxesAiTagging: FC<EmptyInboxesAiTaggingProps> = (props) => {
  const {lensNamespace, lensId, reviewingAiTagId} = props;
  const dispatch = useAppDispatch();
  const localize = useLocalize();

  const {data: aiTaggingSampleMessagesStats} = useAppQuery(
    aiTaggingQueries.sampleMessageStats(lensNamespace, reviewingAiTagId),
  );

  // This should not happen.
  if (!aiTaggingSampleMessagesStats) {
    return null;
  }

  const isDoneReviewing =
    aiTaggingSampleMessagesStats.sampleMessageIds.length >= aiTaggingSampleMessagesStats.maturityCount;

  const {headerImage, header, body} = getHeaderAndBody(isDoneReviewing, localize);

  const onDeleteViewCtaClick = () => dispatch(deleteLensAsyncAction({lensId}));

  return (
    <>
      {headerImage}
      <StyledViewerHeaderDiv>{header}</StyledViewerHeaderDiv>
      <StyledViewerBodyDiv>{body}</StyledViewerBodyDiv>
      {isDoneReviewing && (
        <StyledViewerFooterDiv>
          <Button
            interactionId="empty-viewer-ai-tagging-harvesting-cta"
            onClick={onDeleteViewCtaClick}
            variant={ButtonVariantsEnum.PRIMARY}
            size={VisualSizesEnum.MEDIUM}
          >
            <Trans id="empty-viewer-ai-tagging-harvesting-cta">Delete View</Trans>
          </Button>
        </StyledViewerFooterDiv>
      )}
    </>
  );
};

function getHeaderAndBody(isDoneReviewing: boolean, localize: Localizer) {
  if (isDoneReviewing) {
    return {
      headerImage: (
        <StyledNoConversationsImg
          draggable={false}
          src={successStartGuide4}
          alt={localize(
            defineMessage({
              id: 'empty-ai-review-viewer-all-done',
              message: 'No conversations, all done reviewing',
            }),
          )}
        />
      ),
      header: (
        <Trans id="empty-viewer-ai-tagging-harvesting-header-done-reviewing">
          All samples have been reviewed!
        </Trans>
      ),
      body: (
        <Trans id="empty-viewer-ai-tagging-harvesting-body-done-reviewing">
          We&apos;ve gathered enough samples to train our AI model. This view is no longer needed.
        </Trans>
      ),
    };
  }
  return {
    headerImage: (
      <StyledAllDoneImg
        draggable={false}
        src={inboxNoConversations3}
        alt={localize(
          defineMessage({
            id: 'empty-ai-review-viewer-need-more-messages',
            message: 'No conversations, need more messages to review',
          }),
        )}
      />
    ),
    header: <Trans id="empty-viewer-ai-tagging-harvesting-header-need-more">Hang tight!</Trans>,
    body: (
      <Trans id="empty-viewer-ai-tagging-harvesting-body-need-more">
        We were unable to find any messages in your email history to train this tag on. As we find samples
        from new inbound messages they will appear here for your review.
      </Trans>
    ),
  };
}
